import { useMemo, useState } from "react";
import { SUBDIVISION_OPTIONS } from "@/lib/jurisdiction";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useDeleteSandbox,
  useGenerateSandbox,
  useSandboxes,
  type SandboxGenerateResult,
  type SandboxProvision,
} from "@/api/queries/sandbox";
import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { canManageSandboxes } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";

/**
 * The sandbox form (RES-155): Generate + Manage for Mujeeb and Bryce only.
 * Generate provisions a real tenant (RES-152) with the crafted incident copied
 * in (RES-157); Manage lists the registry and drives the guarded teardown
 * (RES-158) behind a type-the-department-name confirmation.
 */
export function SandboxPage() {
  const { identity } = useAuth();

  if (!canManageSandboxes(identity?.role)) {
    return (
      <p className="text-sm text-destructive">
        Sandbox management is restricted to RespAI sales and platform
        administrators.
      </p>
    );
  }

  return <SandboxPageInner />;
}

function SandboxPageInner() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          Fire Chief Sandbox
        </h1>
        <p className="text-sm text-muted-foreground">
          Generate a branded, per-department demo — landing page, chief
          dashboard and login — and manage every sandbox that exists.
        </p>
      </div>
      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate">Generate</TabsTrigger>
          <TabsTrigger value="manage">Manage</TabsTrigger>
        </TabsList>
        <TabsContent value="generate">
          <GenerateTab />
        </TabsContent>
        <TabsContent value="manage">
          <ManageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Generate ──────────────────────────────────────────────────────────── */

const EMPTY_FORM = {
  departmentName: "",
  // No default on purpose (RES-208): the old hardcoded CA-NL put Newfoundland
  // & Labrador on every prospect's letterhead. The operator picks, every time.
  jurisdiction: "",
  chiefGivenName: "",
  chiefFamilyName: "",
  chiefEmail: "",
  scenario: "",
  contactEmail: "",
};

async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  // Strip the data:<mime>;base64, prefix — the API wants raw base64 bytes.
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function GenerateTab() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // Held in component state ONLY: the temp password is shown exactly once and
  // must never be persisted client-side (no store, no storage, no query cache).
  const [result, setResult] = useState<SandboxGenerateResult | null>(null);
  const generate = useGenerateSandbox();

  const set =
    (key: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const complete =
    form.departmentName.trim() !== "" &&
    form.jurisdiction !== "" &&
    form.chiefGivenName.trim() !== "" &&
    form.chiefFamilyName.trim() !== "" &&
    form.chiefEmail.includes("@") &&
    form.scenario.trim() !== "" &&
    form.contactEmail.includes("@");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete || generate.isPending) return;
    const logo_base64 = logoFile ? await fileToBase64(logoFile) : null;
    generate.mutate(
      {
        department_name: form.departmentName.trim(),
        jurisdiction: form.jurisdiction,
        chief_given_name: form.chiefGivenName.trim(),
        chief_family_name: form.chiefFamilyName.trim(),
        chief_email: form.chiefEmail.trim(),
        scenario: form.scenario.trim(),
        contact_email: form.contactEmail.trim(),
        logo_base64,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setForm(EMPTY_FORM);
          setLogoFile(null);
        },
      },
    );
  };

  if (result) {
    return <GenerateResult result={result} onDismiss={() => setResult(null)} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>New sandbox</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="max-w-xl space-y-4">
          <Field label="Department name">
            <Input
              value={form.departmentName}
              onChange={set("departmentName")}
              placeholder="Springfield Fire Department"
            />
          </Field>
          <Field label="Province or territory (printed on the report letterhead)">
            <select
              data-testid="sandbox-jurisdiction"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.jurisdiction}
              onChange={set("jurisdiction")}
            >
              <option value="" disabled>
                Select where this department is…
              </option>
              {SUBDIVISION_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Chief given name">
              <Input
                value={form.chiefGivenName}
                onChange={set("chiefGivenName")}
                placeholder="Alex"
              />
            </Field>
            <Field label="Chief family name">
              <Input
                value={form.chiefFamilyName}
                onChange={set("chiefFamilyName")}
                placeholder="Morgan"
              />
            </Field>
          </div>
          <Field label="Chief email">
            <Input
              type="email"
              value={form.chiefEmail}
              onChange={set("chiefEmail")}
              placeholder="chief@springfieldfd.ca"
            />
          </Field>
          <Field label="Scenario line">
            <Input
              value={form.scenario}
              onChange={set("scenario")}
              placeholder="Two-storey residential fire with interior attack"
            />
          </Field>
          <Field label="Contact email (shown in the page footer)">
            <Input
              type="email"
              value={form.contactEmail}
              onChange={set("contactEmail")}
              placeholder="sales@respai.ca"
            />
          </Field>
          <Field label="Department logo (PNG or JPEG, optional)">
            <Input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          {generate.isError ? (
            <p className="text-sm text-destructive">
              Generate failed:{" "}
              {generate.error instanceof ApiError
                ? generate.error.message
                : "unexpected error"}
            </p>
          ) : null}
          <Button type="submit" disabled={!complete || generate.isPending}>
            {generate.isPending ? "Generating…" : "Generate sandbox"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function GenerateResult({
  result,
  onDismiss,
}: {
  result: SandboxGenerateResult;
  onDismiss: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sandbox ready</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Copy everything below into your outreach now. The temporary password
          is shown <span className="font-semibold">only this once</span> — it is
          not stored anywhere and cannot be retrieved later.
        </p>
        <dl className="space-y-2">
          <ResultRow label="Landing page" value={result.landing_url} />
          <ResultRow label="Chief dashboard" value={result.dashboard_url} />
          <ResultRow label="Chief username" value={result.chief_email} />
          <ResultRow label="Temporary password" value={result.temp_password} />
        </dl>
        <Button variant="outline" onClick={onDismiss}>
          Done — clear from screen
        </Button>
      </CardContent>
    </Card>
  );
}

function ResultRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <dt className="w-44 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono text-sm">{value}</dd>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

/* ── Manage ────────────────────────────────────────────────────────────── */

function ManageTab() {
  const sandboxes = useSandboxes();
  const [pendingDelete, setPendingDelete] = useState<SandboxProvision | null>(
    null,
  );

  if (sandboxes.isPending) return <Skeleton className="h-40 w-full" />;
  if (sandboxes.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load the sandbox registry.
      </p>
    );
  }

  const rows = sandboxes.data;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sandboxes generated yet.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Generated sandboxes</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <Thead>
              <Tr>
                <Th>Department</Th>
                <Th>Chief email</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Status</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.department_id}>
                  <Td className="font-medium">{row.department_name}</Td>
                  <Td>{row.chief_email}</Td>
                  <Td>{formatDateTime(row.created_at)}</Td>
                  <Td>{row.expires_at ? formatDateTime(row.expires_at) : "—"}</Td>
                  <Td>
                    {row.status === "deleted" ? (
                      <Badge variant="secondary">Deleted</Badge>
                    ) : (
                      <Badge>Active</Badge>
                    )}
                  </Td>
                  <Td>
                    {row.status === "active" ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setPendingDelete(row)}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>
      {pendingDelete ? (
        <DeleteConfirm
          provision={pendingDelete}
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The teardown is real — tenant rows, chief login, files — so the confirm is
 * a typed-name gate, same idea as GitHub repo deletion. Exact match only.
 */
function DeleteConfirm({
  provision,
  onClose,
}: {
  provision: SandboxProvision;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const del = useDeleteSandbox();
  const armed = useMemo(
    () => typed === provision.department_name,
    [typed, provision.department_name],
  );

  return (
    <Card className="border-destructive">
      <CardHeader>
        <CardTitle>Delete “{provision.department_name}”?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          This tears down the whole sandbox: the tenant and every row in it,
          the chief&apos;s login, uploaded files and the landing page. The row
          stays in this list as a deleted record. This cannot be undone.
        </p>
        <label className="block max-w-md space-y-1">
          <span className="text-sm font-medium">
            Type the department name to confirm
          </span>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={provision.department_name}
          />
        </label>
        {del.isError ? (
          <p className="text-sm text-destructive">
            Delete refused:{" "}
            {del.error instanceof ApiError
              ? del.error.message
              : "unexpected error"}
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button
            variant="destructive"
            disabled={!armed || del.isPending}
            onClick={() =>
              del.mutate(provision.department_id, { onSuccess: onClose })
            }
          >
            {del.isPending ? "Deleting…" : "Delete sandbox"}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
